import { useState, useEffect, useRef, useCallback } from 'react'
import api from '../utils/api'

const CONTENT_TYPES = [
  { value: '', label: 'All' },
  { value: 'news', label: 'News' },
  { value: 'editorial', label: 'Editorial' },
  { value: 'scientific', label: 'Scientific' },
  { value: 'regulatory', label: 'Regulatory' },
  { value: 'press_release', label: 'Press Release' },
]

const TYPE_COLORS = {
  news:          { bg: '#8B1A1A', color: '#f0d0d0' },
  editorial:     { bg: '#5c3a1e', color: '#f0d9c0' },
  scientific:    { bg: '#1a4a6e', color: '#c0d8f0' },
  regulatory:    { bg: '#2d4a1a', color: '#c8e0c0' },
  press_release: { bg: '#3a1a5c', color: '#d8c8f0' },
}

const S = {
  container: { fontFamily: "'DM Sans', sans-serif", color: '#e0e0e0' },
  header: { marginBottom: '1.5rem' },
  title: { fontSize: '1.4rem', fontWeight: 700, color: '#e8e8e8', margin: 0, marginBottom: '0.25rem', letterSpacing: '-0.01em' },
  subtitle: { fontSize: '0.8rem', color: '#bbb', margin: 0 },
  searchWrap: { position: 'relative', marginBottom: '1rem' },
  searchInput: {
    width: '100%', boxSizing: 'border-box',
    background: '#111', border: '1px solid #222', borderRadius: '6px',
    color: '#e0e0e0', fontSize: '0.88rem', padding: '0.6rem 0.75rem 0.6rem 2.4rem',
    outline: 'none',
  },
  searchIcon: { position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#bbb', fontSize: '1rem', pointerEvents: 'none' },
  filterRow: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1.5rem' },
  chip: (active) => ({
    padding: '0.3rem 0.85rem', borderRadius: '20px', fontSize: '0.78rem', fontWeight: 500,
    cursor: 'pointer', border: active ? '1px solid #8B1A1A' : '1px solid #222',
    background: active ? 'rgba(139,26,26,0.25)' : '#111',
    color: active ? '#f0a0a0' : '#bbb', transition: 'all 0.15s',
  }),
  grid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' },
  card: {
    background: '#111', border: '1px solid #1c1c1c', borderRadius: '8px',
    padding: '1.1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem',
    transition: 'border-color 0.15s',
  },
  cardMeta: { display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' },
  sourceBadge: (type) => ({
    padding: '0.15rem 0.55rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600,
    background: TYPE_COLORS[type]?.bg || '#2a1a1a',
    color: TYPE_COLORS[type]?.color || '#f0c0c0',
    textTransform: 'uppercase', letterSpacing: '0.06em',
  }),
  sourceText: {
    fontSize: '0.75rem', color: '#8B1A1A', fontWeight: 500,
    background: 'rgba(139,26,26,0.12)', padding: '0.15rem 0.5rem', borderRadius: '4px',
  },
  date: { fontSize: '0.72rem', color: '#B8860B', marginLeft: 'auto' },
  cardTitle: { fontSize: '0.93rem', fontWeight: 600, color: '#e0e0e0', lineHeight: 1.4, margin: 0 },
  bodyPreview: { fontSize: '0.8rem', color: '#d0d0d0', lineHeight: 1.6, margin: 0 },
  readMore: {
    background: 'none', border: 'none', color: '#8B1A1A', fontSize: '0.75rem',
    cursor: 'pointer', padding: 0, marginTop: '0.25rem', textDecoration: 'underline', alignSelf: 'flex-start',
  },
  bodyExpanded: { fontSize: '0.8rem', color: '#d0d0d0', lineHeight: 1.7, margin: 0, whiteSpace: 'pre-wrap' },
  externalLink: {
    display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
    color: '#bbb', fontSize: '0.72rem', textDecoration: 'none', marginTop: '0.25rem',
  },
  loadMoreWrap: { marginTop: '1.5rem', textAlign: 'center' },
  loadMoreBtn: {
    background: '#111', border: '1px solid #222', borderRadius: '6px',
    color: '#bbb', fontSize: '0.82rem', padding: '0.6rem 2rem', cursor: 'pointer',
    transition: 'border-color 0.15s, color 0.15s',
  },
  empty: { textAlign: 'center', color: '#bbb', fontSize: '0.88rem', fontStyle: 'italic', padding: '4rem 0' },
  skeletonCard: {
    background: '#111', border: '1px solid #1c1c1c', borderRadius: '8px',
    padding: '1.1rem', display: 'flex', flexDirection: 'column', gap: '0.6rem',
  },
  skeletonLine: (w, h) => ({
    background: '#1a1a1a', borderRadius: '4px', height: h || '12px', width: w || '100%',
    animation: 'pulse 1.4s ease-in-out infinite',
  }),
}

function formatDate(str) {
  if (!str) return ''
  try {
    return new Date(str).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
  } catch { return '' }
}

function SkeletonCard() {
  return (
    <div style={S.skeletonCard}>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <div style={S.skeletonLine('60px', '18px')} />
        <div style={S.skeletonLine('80px', '18px')} />
        <div style={{ ...S.skeletonLine('70px', '18px'), marginLeft: 'auto' }} />
      </div>
      <div style={S.skeletonLine('85%', '16px')} />
      <div style={S.skeletonLine('70%', '16px')} />
      <div style={S.skeletonLine('100%', '12px')} />
      <div style={S.skeletonLine('90%', '12px')} />
      <div style={S.skeletonLine('60%', '12px')} />
    </div>
  )
}

function ArticleCard({ article }) {
  const [expanded, setExpanded] = useState(false)
  const preview = article.body_preview || ''
  const short = preview.slice(0, 200)
  const hasMore = preview.length > 200

  return (
    <div style={S.card}>
      <div style={S.cardMeta}>
        <span style={S.sourceBadge(article.content_type)}>{article.content_type}</span>
        {article.source && <span style={S.sourceText}>{article.source}</span>}
        <span style={S.date}>{formatDate(article.published_at)}</span>
      </div>
      <p style={S.cardTitle}>{article.title}</p>
      {article.author && (
        <div style={{ fontSize: '0.72rem', color: '#bbb' }}>by {article.author}</div>
      )}
      {preview && (
        <>
          <p style={expanded ? S.bodyExpanded : S.bodyPreview}>
            {expanded ? preview : short}
            {!expanded && hasMore && '…'}
          </p>
          {hasMore && (
            <button style={S.readMore} onClick={() => setExpanded(e => !e)}>
              {expanded ? 'Show less' : 'Read more'}
            </button>
          )}
        </>
      )}
      {article.url && (
        <a href={article.url} target="_blank" rel="noopener noreferrer" style={S.externalLink}>
          <span>&#8599;</span> Source
        </a>
      )}
    </div>
  )
}

export default function NewsArchive() {
  const [articles, setArticles] = useState([])
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 })
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [contentType, setContentType] = useState('')
  const [draftQ, setDraftQ] = useState('')
  const [q, setQ] = useState('')
  const debounceRef = useRef(null)

  const fetchNews = useCallback(async (opts = {}) => {
    const { append = false, page = 1 } = opts
    if (!append) setLoading(true)
    else setLoadingMore(true)

    try {
      const params = new URLSearchParams({ page, limit: 20 })
      if (q) params.set('q', q)
      if (contentType) params.set('content_type', contentType)

      const res = await api.get(`/data/news?${params}`)
      const data = res.data

      if (append) {
        setArticles(prev => [...prev, ...data.articles])
      } else {
        setArticles(data.articles)
      }
      setPagination(data.pagination)
    } catch (err) {
      console.error('news fetch error:', err)
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [q, contentType])

  useEffect(() => {
    fetchNews({ page: 1 })
  }, [fetchNews])

  const handleSearchChange = (e) => {
    const val = e.target.value
    setDraftQ(val)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setQ(val), 400)
  }

  const handleLoadMore = () => {
    fetchNews({ append: true, page: pagination.page + 1 })
  }

  const hasMore = pagination.page < pagination.pages

  return (
    <div style={S.container}>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        .news-card:hover { border-color: #2a2a2a !important; }
        .load-more-btn:hover { border-color: #444 !important; color: #aaa !important; }
      `}</style>

      <div style={S.header}>
        <h2 style={S.title}>News Archive</h2>
        <p style={S.subtitle}>Menhaden policy news, editorials, and regulatory updates</p>
      </div>

      <div style={S.searchWrap}>
        <span style={S.searchIcon}>&#128269;</span>
        <input
          type="text"
          placeholder="Search articles…"
          value={draftQ}
          onChange={handleSearchChange}
          style={S.searchInput}
        />
      </div>

      <div style={S.filterRow}>
        {CONTENT_TYPES.map(ct => (
          <button
            key={ct.value}
            style={S.chip(contentType === ct.value)}
            onClick={() => setContentType(ct.value)}
          >
            {ct.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={S.grid}>
          {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      ) : articles.length === 0 ? (
        <div style={S.empty}>No articles yet. Check back soon.</div>
      ) : (
        <>
          <div style={S.grid}>
            {articles.map(a => <ArticleCard key={a.id} article={a} />)}
            {loadingMore && Array.from({ length: 2 }).map((_, i) => <SkeletonCard key={`more-${i}`} />)}
          </div>
          {hasMore && (
            <div style={S.loadMoreWrap}>
              <button
                className="load-more-btn"
                style={S.loadMoreBtn}
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading…' : `Load more (${pagination.total - articles.length} remaining)`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
